import React, { type FC, useState } from "react";
import { useI18next } from "gatsby-plugin-react-i18next";

import ChatCloseButton from "@components/chat/chat_close_button";
import { useAuthenticationState } from "@stores/authentication";
import type { CcStatus } from "@stores/customer_care";
import { getSupportBoardUrl } from "@utilities/base-url";

/**
 * What the panel shows when the Customer Care API is not usable:
 * - `fallback` (login 404: the server's customer-chat flag is off, or no CC
 *   URL configured): the legacy support-board iframe, so support keeps
 *   working until the flag is on; without a board URL, the unavailable note.
 * - `conflict` (403): chat is not available for this account.
 * - `unavailable` (502 / network): temporarily unavailable, with retry.
 */
const supportEmail = "support@bnesim.com";

const ChatFallback: FC<{
  status: CcStatus;
  onRetry: () => void;
  onClose: () => void;
}> = ({ status, onRetry, onClose }) => {
  const { t } = useI18next();
  const authenticationState = useAuthenticationState();
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const boardUrl = getSupportBoardUrl();

  // The chat's close, above whatever the view shows.
  const closeBar = (
    <div className="flex shrink-0 justify-end px-4 pt-3">
      <ChatCloseButton onClose={onClose} />
    </div>
  );

  if (status === "fallback" && boardUrl !== "") {
    const params = new URLSearchParams({
      hide_close: "1",
      t: authenticationState.getToken(),
    });
    return (
      <>
        {closeBar}
        <div className="relative min-h-px w-full flex-1">
          {!isIframeLoaded && (
            // A conversation's shape (two replies, one message, the
            // composer) until the support board loads.
            <div
              role="status"
              aria-label={t("chat_loading")}
              className="absolute inset-0 flex animate-pulse flex-col gap-3 p-4"
            >
              <span className="h-12 w-3/5 rounded-2xl rounded-es-none bg-black/5" />
              <span className="h-16 w-2/3 rounded-2xl rounded-es-none bg-black/5" />
              <span className="h-12 w-1/2 self-end rounded-2xl rounded-ee-none bg-primary/10" />
              <span className="min-h-px flex-1" />
              <span className="h-12 w-full rounded-full bg-black/5" />
            </div>
          )}
          <iframe
            src={`${boardUrl}?${params.toString()}`}
            onLoad={() => {
              setIframeLoaded(true);
            }}
            title={t("live_chat")}
            className="size-full"
          />
        </div>
      </>
    );
  }

  const message =
    status === "conflict"
      ? t("chat_unavailable_account")
      : status === "unavailable"
        ? t("chat_temporarily_unavailable")
        : t("chat_unavailable");

  return (
    <>
      {closeBar}
      <div className="flex min-h-px w-full flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="mb-0 text-base font-semibold text-black">{message}</p>
        <a
          className="text-sm font-semibold text-primary underline-offset-2 hover:underline"
          href={`mailto:${supportEmail}`}
        >
          {supportEmail}
        </a>
        {status === "unavailable" && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-full border border-black/15 bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
          >
            {t("chat_retry")}
          </button>
        )}
      </div>
    </>
  );
};

export default ChatFallback;
